import { KrCard } from '@/common/card/KrCard';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useRef, useState } from 'react';

export const Metrics = () => {
  const module = useModuleFromRoute();
  const rsrc = useRsrc();

  const endpointsGridRef = useRef(null);
  const requestsGridRef = useRef(null);
  const processesGridRef = useRef(null);
  const jvmGridRef = useRef(null);
  const threadsGridRef = useRef(null);
  const cacheGridRef = useRef(null);
  const diskGridRef = useRef(null);
  const rawGridRef = useRef(null);
  const dbGridRef = useRef(null);
  const gcGridRef = useRef(null);

  const [data, setData] = useState(null);
  const [collapsedCards, setCollapsedCards] = useState({
    jvm: true,
    gc: true,
    db: true,
    raw: true,
    threads: true,
  });

  useEffect(() => {
    rsrc.metrics(module).then((data) => {
      setData(data);
    });
  }, [module]);

  const requestsColDefs = [
    { field: 'code', headerName: 'Status', minWidth: 90 },
    { field: 'count', headerName: 'Count', valueFormatter: ({ value }) => formatNumber(value), minWidth: 110 },
    { field: 'mean', headerName: 'Mean ms', valueFormatter: ({ value }) => formatNumber(value, 0), minWidth: 110 },
    { field: 'max', headerName: 'Max ms', valueFormatter: ({ value }) => formatNumber(value, 0), minWidth: 110 },
  ];

  const endpointsColDefs = [
    { field: 'endpoint', headerName: 'Endpoint', flex: 1, minWidth: 220 },
    { field: 'method', headerName: 'Method', width: 110 },
    { field: 'count', headerName: 'Count', valueFormatter: ({ value }) => formatNumber(value), minWidth: 110 },
    { field: 'mean', headerName: 'Mean ms', valueFormatter: ({ value }) => formatNumber(value, 0), minWidth: 110 },
    { field: 'max', headerName: 'Max ms', valueFormatter: ({ value }) => formatNumber(value, 0), minWidth: 110 },
  ];

  const processesColDefs = [
    { field: 'metric', headerName: 'Metric', flex: 1, minWidth: 220 },
    { field: 'value', headerName: 'Value', valueFormatter: ({ value }) => formatNumber(value, 3), minWidth: 110 },
  ];

  const jvmColDefs = [
    { field: 'pool', headerName: 'Pool', flex: 1, minWidth: 160 },
    { field: 'used', headerName: 'Used', valueFormatter: ({ value }) => formatBytes(value), minWidth: 120 },
    { field: 'committed', headerName: 'Commit', valueFormatter: ({ value }) => formatBytes(value), minWidth: 120 },
    { field: 'max', headerName: 'Max', valueFormatter: ({ value }) => formatBytes(value), minWidth: 120 },
    {
      field: 'usedPct',
      headerName: 'Used %',
      valueFormatter: ({ value }) => (value == null ? '—' : `${value.toFixed(1)}%`),
      minWidth: 110,
    },
  ];

  const cacheColDefs = [
    { field: 'cache', headerName: 'Cache', flex: 1, minWidth: 160 },
    { field: 'metric', headerName: 'Metric', flex: 1, minWidth: 180 },
    { field: 'value', headerName: 'Value', valueFormatter: ({ value }) => formatNumber(value, 3), minWidth: 110 },
  ];

  const threadsColDefs = [
    { field: 'metric', headerName: 'Metric', flex: 1, minWidth: 220 },
    { field: 'value', headerName: 'Value', valueFormatter: ({ value }) => formatNumber(value, 0), minWidth: 110 },
  ];

  const diskColDefs = [
    { field: 'path', headerName: 'Path', flex: 1, minWidth: 220 },
    { field: 'metric', headerName: 'Metric', flex: 1, minWidth: 180 },
    { field: 'value', headerName: 'Value', valueFormatter: ({ value }) => formatBytes(value), minWidth: 120 },
  ];

  const rawColDefs = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
    { field: 'type', headerName: 'Type', width: 110 },
    { field: 'baseUnit', headerName: 'Unit', width: 110 },
    { field: 'tags', headerName: 'Tags', flex: 1, minWidth: 220, valueFormatter: ({ value }) => formatMap(value) },
    {
      field: 'measurements',
      headerName: 'Measurements',
      flex: 1,
      minWidth: 240,
      valueFormatter: ({ value }) => formatMap(value, 3),
    },
  ];

  const dbColDefs = [
    { field: 'pool', headerName: 'Pool', flex: 1, minWidth: 160 },
    { field: 'metric', headerName: 'Metric', flex: 1, minWidth: 180 },
    { field: 'value', headerName: 'Value', valueFormatter: ({ value }) => formatNumber(value, 3), minWidth: 110 },
  ];

  const gcColDefs = [
    { field: 'metric', headerName: 'Metric', flex: 1, minWidth: 220 },
    { field: 'value', headerName: 'Value', valueFormatter: ({ value }) => formatNumber(value, 3), minWidth: 110 },
  ];

  const title = (module ? module.charAt(0).toUpperCase() + module.slice(1) : 'Module') + ' Metrics';

  const formatNumber = (value, digits = 0) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });
  };

  const formatMs = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${formatNumber(value, 0)} ms`;
  };

  const formatBytes = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    if (Number(value) < 0) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = Number(value);
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    return `${size.toFixed(size >= 100 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const formatMap = (value, digits = 0) => {
    if (!value || typeof value !== 'object') return '—';
    return Object.entries(value)
      .map(([key, entryValue]) => {
        if (entryValue == null || Number.isNaN(Number(entryValue))) {
          return `${key}: —`;
        }
        if (typeof entryValue === 'number') {
          return `${key}: ${formatNumber(entryValue, digits)}`;
        }
        return `${key}: ${entryValue}`;
      })
      .join(', ');
  };

  const toKeyValueRows = (obj, keyName, valueName) => {
    if (!obj) return [];
    return Object.entries(obj).map(([key, value]) => ({ [keyName]: key, [valueName]: value }));
  };

  const {
    processesDataSrc,
    requestsDataSrc,
    endpointsDataSrc,
    jvmDataSrc,
    threadsDataSrc,
    cacheDataSrc,
    diskDataSrc,
    rawDataSrc,
    dbDataSrc,
    gcDataSrc,
  } = useMemo(() => {
    const processesDataSrc = toKeyValueRows(data?.processMetrics, 'metric', 'value');

    const reqObj = data?.['http.server.requests'] || {};
    const perCodeObj = reqObj.percode || {};
    const requestsDataSrc = Object.entries(perCodeObj).map(([code, stats]) => ({
      code,
      mean: stats.mean,
      max: stats.max,
      count: stats.count,
    }));

    const endpointsDataSrc = Object.entries(data?.services || {}).flatMap(([endpoint, typeToStats]) => {
      return Object.entries(typeToStats).map(([method, stats]) => ({
        endpoint,
        method,
        max: stats.max,
        mean: stats.mean,
        count: stats.count,
      }));
    });

    const jvmDataSrc = Object.entries(data?.jvm || {}).map(([pool, stats]) => ({
      pool,
      used: stats.used,
      committed: stats.committed,
      max: stats.max,
      usedPct: stats.max <= 0 ? null : (stats.used / stats.max) * 100,
    }));

    const threadsSummary = toKeyValueRows(data?.jvmThreads?.summary, 'metric', 'value');
    const threadsStates = toKeyValueRows(data?.jvmThreads?.states, 'state', 'value').map(({ state, value }) => ({
      metric: `state.${state}`,
      value,
    }));
    const threadsDataSrc = [...threadsSummary, ...threadsStates];

    const cacheDataSrc = Object.entries(data?.cache || {}).flatMap(([cache, metrics]) => {
      return Object.entries(metrics || {}).map(([metric, value]) => ({ cache, metric, value }));
    });

    const diskDataSrc = Object.entries(data?.disk || {}).flatMap(([path, metrics]) => {
      return Object.entries(metrics || {}).map(([metric, value]) => ({ path, metric, value }));
    });

    const rawDataSrc = (data?.raw?.meters || []).map((meter) => ({
      name: meter.name,
      type: meter.type,
      baseUnit: meter.baseUnit,
      tags: meter.tags,
      measurements: meter.measurements,
    }));

    const dbDataSrc = Object.entries(data?.databases || {}).flatMap(([pool, metrics]) => {
      return Object.entries(metrics || {}).map(([metric, value]) => ({ pool, metric, value }));
    });

    const gcDataSrc = Object.entries(data?.garbageCollector || {}).flatMap(([metric, value]) => {
      if (value != null && typeof value === 'object') {
        return Object.entries(value).map(([sub, subValue]) => ({ metric: `${metric}.${sub}`, value: subValue }));
      }
      return [{ metric, value }];
    });

    return {
      processesDataSrc,
      requestsDataSrc,
      endpointsDataSrc,
      jvmDataSrc,
      threadsDataSrc,
      cacheDataSrc,
      diskDataSrc,
      rawDataSrc,
      dbDataSrc,
      gcDataSrc,
    };
  }, [data]);

  const requestTotals = useMemo(() => {
    const total = requestsDataSrc.reduce((sum, row) => sum + (row.count || 0), 0);
    const weightedMean =
      total > 0 ? requestsDataSrc.reduce((sum, row) => sum + (row.mean || 0) * (row.count || 0), 0) / total : null;
    const max = requestsDataSrc.reduce((currentMax, row) => Math.max(currentMax, row.max || 0), 0);
    return { total, weightedMean, max };
  }, [requestsDataSrc]);

  const endpointTotals = useMemo(() => {
    const total = endpointsDataSrc.reduce((sum, row) => sum + (row.count || 0), 0);
    const busiest = [...endpointsDataSrc].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 5);
    const slowest = [...endpointsDataSrc].sort((a, b) => (b.mean || 0) - (a.mean || 0)).slice(0, 5);
    return { total, busiest, slowest };
  }, [endpointsDataSrc]);

  const heapSummary = useMemo(() => {
    const heapPools = new Set(['g1 eden space', 'g1 survivor space', 'g1 old gen']);
    const heapRows = jvmDataSrc.filter((row) => heapPools.has(String(row.pool || '').toLowerCase()));
    if (heapRows.length === 0) return null;
    const used = heapRows.reduce((sum, row) => sum + (Number(row.used) || 0), 0);
    const max = heapRows.reduce((sum, row) => sum + (Number(row.max) || 0), 0);
    const usedPct = max <= 0 ? null : (used / max) * 100;
    return { used, max, usedPct };
  }, [jvmDataSrc]);

  const pageStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  };

  const contentStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: '4px',
    overscrollBehaviorY: 'contain',
  };

  const gridOptionsBase = {
    rowModelType: 'clientSide',
    headerHeight: 48,
    defaultColDef: {
      wrapHeaderText: false,
      autoHeaderHeight: false,
    },
  };

  const dashboardGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    gap: '16px',
    alignItems: 'start',
  };

  const gridWrapperStyle = {
    height: '420px',
    minHeight: '360px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  };

  const compactGridWrapperStyle = {
    height: '210px',
    minHeight: '180px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  };

  const tallGridWrapperStyle = {
    height: '840px',
    minHeight: '720px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  };

  const summaryRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
  };

  const cardWrapperStyle = {
    minWidth: 0,
    width: '100%',
    breakInside: 'avoid',
    display: 'inline-block',
    marginBottom: '16px',
  };

  const insightsListStyle = {
    paddingLeft: '18px',
    margin: 0,
    lineHeight: 1.5,
  };

  const insightsItemStyle = {
    marginBottom: '12px',
  };

  const collapseButtonStyle = {
    background: 'transparent',
    border: '1px solid #bdbdbd',
    borderRadius: '12px',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: '12px',
    padding: 0,
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  };

  const toggleCard = (key) => {
    setCollapsedCards((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderCard = (key, title, testId, children, isCollapsible = true) => (
    <div style={cardWrapperStyle}>
      <KrCard
        title={title}
        data-test-id={testId}
        buttons={
          isCollapsible ?
            <button
              type="button"
              onClick={() => toggleCard(key)}
              style={collapseButtonStyle}
              aria-expanded={!collapsedCards[key]}
              aria-label={collapsedCards[key] ? 'Expand section' : 'Collapse section'}
              title={collapsedCards[key] ? 'Expand section' : 'Collapse section'}
            >
              <FontAwesomeIcon icon={collapsedCards[key] ? far.faChevronDown : far.faChevronUp} />
            </button>
          : undefined
        }
      >
        {(!isCollapsible || !collapsedCards[key]) && children}
      </KrCard>
    </div>
  );

  if (!data) return null;

  return (
    <div style={pageStyle}>
      <Title icon={far.faListUl} title={title} />
      <div style={contentStyle}>
        <div style={dashboardGridStyle}>
          {renderCard(
            'summary',
            'At a Glance',
            'metrics-summary',
            <div style={summaryRowStyle}>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>Total Requests</div>
                <div style={{ fontSize: '24px', fontWeight: 600 }}>{formatNumber(requestTotals.total)}</div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>Weighted Mean Latency</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{formatMs(requestTotals.weightedMean)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>Tracked Endpoints</div>
                <div style={{ fontSize: '24px', fontWeight: 600 }}>{formatNumber(endpointsDataSrc.length)}</div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>Endpoint Request Count</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{formatNumber(endpointTotals.total)}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>Peak Status Latency</div>
                <div style={{ fontSize: '24px', fontWeight: 600 }}>{formatMs(requestTotals.max)}</div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>Heap Used</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>
                  {heapSummary ? `${formatBytes(heapSummary.used)} (${heapSummary.usedPct?.toFixed(1)}%)` : '—'}
                </div>
              </div>
            </div>,
            false,
          )}

          {renderCard(
            'insights',
            'Endpoint Insights',
            'endpoint-insights',
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Slowest (Mean)</div>
                <ol style={insightsListStyle}>
                  {endpointTotals.slowest.length === 0 && <li>—</li>}
                  {endpointTotals.slowest.map((row) => (
                    <li key={`${row.method}-${row.endpoint}`} style={insightsItemStyle}>
                      <div style={{ fontSize: '13px', fontWeight: 600, wordBreak: 'break-word' }}>
                        {row.method} {row.endpoint}
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.7 }}>
                        Mean {formatMs(row.mean)} • Count {formatNumber(row.count)}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Busiest (Count)</div>
                <ol style={insightsListStyle}>
                  {endpointTotals.busiest.length === 0 && <li>—</li>}
                  {endpointTotals.busiest.map((row) => (
                    <li key={`${row.method}-${row.endpoint}-count`} style={insightsItemStyle}>
                      <div style={{ fontSize: '13px', fontWeight: 600, wordBreak: 'break-word' }}>
                        {row.method} {row.endpoint}
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.7 }}>
                        Count {formatNumber(row.count)} • Mean {formatMs(row.mean)}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>,
            false,
          )}

          {renderCard(
            'endpoints',
            'Endpoint Metrics',
            'endpoint-metrics',
            <div style={gridWrapperStyle}>
              <Grid
                ref={endpointsGridRef}
                gridOptions={gridOptionsBase}
                events={{
                  onGridReady: () => {
                    if (endpointsGridRef.current) {
                      endpointsGridRef.current.setColDefs(endpointsColDefs);
                      endpointsGridRef.current.setDataSource(endpointsDataSrc);
                    }
                  },
                }}
              />
            </div>,
          )}

          {renderCard(
            'httpStatus',
            'HTTP Status Metrics',
            'http-status-metrics',
            <div style={compactGridWrapperStyle}>
              <Grid
                ref={requestsGridRef}
                gridOptions={gridOptionsBase}
                events={{
                  onGridReady: () => {
                    if (requestsGridRef.current) {
                      requestsGridRef.current.setColDefs(requestsColDefs);
                      requestsGridRef.current.setDataSource(requestsDataSrc);
                    }
                  },
                }}
              />
            </div>,
          )}

          {renderCard(
            'processes',
            'Process Metrics',
            'process-metrics',
            <div style={gridWrapperStyle}>
              <Grid
                ref={processesGridRef}
                gridOptions={gridOptionsBase}
                events={{
                  onGridReady: () => {
                    if (processesGridRef.current) {
                      processesGridRef.current.setColDefs(processesColDefs);
                      processesGridRef.current.setDataSource(processesDataSrc);
                    }
                  },
                }}
              />
            </div>,
          )}

          {diskDataSrc.length > 0 &&
            renderCard(
              'disk',
              'Disk Metrics',
              'disk-metrics',
              <div style={compactGridWrapperStyle}>
                <Grid
                  ref={diskGridRef}
                  gridOptions={gridOptionsBase}
                  events={{
                    onGridReady: () => {
                      if (diskGridRef.current) {
                        diskGridRef.current.setColDefs(diskColDefs);
                        diskGridRef.current.setDataSource(diskDataSrc);
                      }
                    },
                  }}
                />
              </div>,
            )}

          {renderCard(
            'jvm',
            'JVM Memory',
            'jvm-metrics',
            <div style={gridWrapperStyle}>
              <Grid
                ref={jvmGridRef}
                gridOptions={gridOptionsBase}
                events={{
                  onGridReady: () => {
                    if (jvmGridRef.current) {
                      jvmGridRef.current.setColDefs(jvmColDefs);
                      jvmGridRef.current.setDataSource(jvmDataSrc);
                    }
                  },
                }}
              />
            </div>,
          )}

          {threadsDataSrc.length > 0 &&
            renderCard(
              'threads',
              'JVM Threads',
              'jvm-threads-metrics',
              <div style={gridWrapperStyle}>
                <Grid
                  ref={threadsGridRef}
                  gridOptions={gridOptionsBase}
                  events={{
                    onGridReady: () => {
                      if (threadsGridRef.current) {
                        threadsGridRef.current.setColDefs(threadsColDefs);
                        threadsGridRef.current.setDataSource(threadsDataSrc);
                      }
                    },
                  }}
                />
              </div>,
            )}

          {renderCard(
            'gc',
            'Garbage Collector',
            'gc-metrics',
            <div style={gridWrapperStyle}>
              <Grid
                ref={gcGridRef}
                gridOptions={gridOptionsBase}
                events={{
                  onGridReady: () => {
                    if (gcGridRef.current) {
                      gcGridRef.current.setColDefs(gcColDefs);
                      gcGridRef.current.setDataSource(gcDataSrc);
                    }
                  },
                }}
              />
            </div>,
          )}

          {cacheDataSrc.length > 0 &&
            renderCard(
              'cache',
              'Cache Metrics',
              'cache-metrics',
              <div style={gridWrapperStyle}>
                <Grid
                  ref={cacheGridRef}
                  gridOptions={gridOptionsBase}
                  events={{
                    onGridReady: () => {
                      if (cacheGridRef.current) {
                        cacheGridRef.current.setColDefs(cacheColDefs);
                        cacheGridRef.current.setDataSource(cacheDataSrc);
                      }
                    },
                  }}
                />
              </div>,
            )}

          {renderCard(
            'db',
            'Database Metrics',
            'db-metrics',
            <div style={gridWrapperStyle}>
              <Grid
                ref={dbGridRef}
                gridOptions={gridOptionsBase}
                events={{
                  onGridReady: () => {
                    if (dbGridRef.current) {
                      dbGridRef.current.setColDefs(dbColDefs);
                      dbGridRef.current.setDataSource(dbDataSrc);
                    }
                  },
                }}
              />
            </div>,
          )}

          {rawDataSrc.length > 0 &&
            renderCard(
              'raw',
              'Raw Meter Registry',
              'raw-metrics',
              <div style={tallGridWrapperStyle}>
                <Grid
                  ref={rawGridRef}
                  gridOptions={gridOptionsBase}
                  events={{
                    onGridReady: () => {
                      if (rawGridRef.current) {
                        rawGridRef.current.setColDefs(rawColDefs);
                        rawGridRef.current.setDataSource(rawDataSrc);
                      }
                    },
                  }}
                />
              </div>,
            )}
        </div>
      </div>
    </div>
  );
};
